import React from 'react';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Label, Error, Input } from 'components/Form'
import { WrapField, WrapFieldContent, BootstrapDatePicker, WrapDatePicker } from './Styled'
import { thirdly } from 'styles/theme';

/**
 * DatePicker (MUI X DatePicker + dayjs)
 * Date field for forms. Wraps MUI DatePicker with dayjs adapter, label, and error display.
 *
 * **How it works**
 * - Pass form state via `input`: `{ value, onChange }`.
 * - `value` should be a dayjs object or `null`/`undefined`.
 * - On change, `onChange` is called with a **dayjs instance** (not a native Date or string).
 * - Set `currentDateByDefault` to pre-fill today when `value` is empty (display only until user changes).
 * - Set `isLocked` to render a read-only `Input` instead of the picker.
 *
 * Exported as `DatePicker` from `components/Form`. Internal name: `CreateDatePicker`.
 *
 * @param {Object} props
 * @param {Object} props.input - Form binding: `{ value, onChange }`.
 * @param {import('dayjs').Dayjs|null} [props.input.value] - Selected date as dayjs.
 * @param {Function} props.input.onChange - Called with `(date: dayjs)` on selection.
 * @param {string} [props.label] - Field label above the picker.
 * @param {string} [props.placeholder] - Placeholder shown inside the picker input.
 * @param {string} [props.format='DD MM YYYY'] - Display format passed to MUI DatePicker.
 * @param {boolean} [props.mandatory] - Shows required indicator on the label.
 * @param {boolean} [props.isLocked] - Renders locked `Input` instead of the date picker.
 * @param {boolean} [props.currentDateByDefault] - Shows today's date when `value` is empty.
 * @param {string} [props.name] - Input name forwarded to LocalizationProvider / picker.
 * @param {string} [props.className] - CSS class on the field wrapper.
 * @param {string} [props.gridArea] - Grid area name; combined with `arrayIndex`.
 * @param {number} [props.arrayIndex] - Index suffix for grid placement.
 * @param {boolean} [props.systemUI=true] - Applies system UI styling to the picker.
 * @param {number|string} [props.borderRadius] - Custom border radius.
 * @example
 * const [date, setDate] = useState(null);
 * <DatePicker
 *   label="Due date"
 *   input={{ value: date, onChange: setDate }}
 * />
 * @example
 * // In form field mapping
 * datePicker: (props) => <DatePicker {...props} />
 */
const CreateDatePicker = (props) => {
    const
        { isLocked, borderRadius, placeholder, gridArea, arrayIndex, name, currentDateByDefault, systemUI = true, label, input, className, mandatory, format = "DD MM YYYY" } = props,
        { onChange, value } = input

    const handleChange = (e) => {
        onChange(dayjs(e))
    }

    let dateValue = value || null
    if (!value && currentDateByDefault)
        dateValue = dayjs(new Date())

    return (
        isLocked ?
            <Input {...props} /> :
            <WrapField className={className} gridArea={`${gridArea}_${arrayIndex}`}>
                <WrapFieldContent>
                    {label && <Label label={label} mandatory={mandatory} />}
                    <LocalizationProvider name={name} dateAdapter={AdapterDayjs}>
                        <BootstrapDatePicker
                            borderRadius={borderRadius}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    variant: "outlined",
                                    color: "thirdly",
                                }
                            }}
                            color={thirdly}
                            label={placeholder}
                            name={name}
                            $systemUI={systemUI}
                            format={format}

                            value={dateValue}
                            onChange={handleChange}
                        />
                    </LocalizationProvider>
                </WrapFieldContent>
                <Error {...props} />
            </WrapField>
    );
}

export default CreateDatePicker